import { z } from "zod";

export const BOOKING_STATUSES = ["new", "replied", "confirmed", "declined"] as const;
export const bookingStatusSchema = z.enum(BOOKING_STATUSES);
export type BookingStatus = z.infer<typeof bookingStatusSchema>;

export const MIN_NIGHTS = 2;
export const MAX_GUESTS = 4;

/** `YYYY-MM-DD`, kept as a plain date so a stay never shifts across timezones. */
const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Use the format YYYY-MM-DD")
  .refine((value) => !Number.isNaN(Date.parse(`${value}T00:00:00Z`)), "That date does not exist");

export const bookingRequestSchema = z
  .object({
    checkIn: isoDate,
    checkOut: isoDate,
    guests: z.number().int().min(1).max(MAX_GUESTS),
    fullName: z.string().trim().min(2, "Tell us who is coming").max(120),
    email: z.string().trim().email("Enter a complete email address").max(200),
    phoneCountry: z.string().trim().min(2).max(6).default("+62"),
    phone: z.string().trim().min(5, "Enter a reachable number").max(30),
    notes: z.string().trim().max(2000).optional().or(z.literal("")),
    acceptedHouseRules: z.literal(true, {
      errorMap: () => ({ message: "Please confirm you have read the house rules" }),
    }),
    locale: z.enum(["en", "id"]).default("en"),
  })
  .refine((v) => v.checkOut > v.checkIn, {
    message: "Check-out must be after check-in",
    path: ["checkOut"],
  })
  .refine((v) => nightsBetween(v.checkIn, v.checkOut) >= MIN_NIGHTS, {
    message: `Minimum stay is ${MIN_NIGHTS} nights`,
    path: ["checkOut"],
  });

/** Validated payload the API receives. */
export type BookingRequestInput = z.output<typeof bookingRequestSchema>;

/**
 * What the form holds *before* validation — fields with `.default()` are still
 * optional here. React Hook Form needs this shape for its resolver generics.
 */
export type BookingRequestValues = z.input<typeof bookingRequestSchema>;

export const bookingSchema = z.object({
  id: z.string().uuid(),
  reference: z.string(),
  checkIn: z.string(),
  checkOut: z.string(),
  nights: z.number().int(),
  guests: z.number().int(),
  fullName: z.string(),
  email: z.string(),
  phoneCountry: z.string(),
  phone: z.string(),
  notes: z.string().nullable(),
  status: bookingStatusSchema,
  locale: z.string(),
  createdAt: z.string(),
});
export type Booking = z.infer<typeof bookingSchema>;

/** What the Thank You page needs — never the full record. */
export const bookingReceiptSchema = bookingSchema.pick({
  reference: true,
  checkIn: true,
  checkOut: true,
  nights: true,
  guests: true,
  email: true,
});
export type BookingReceipt = z.infer<typeof bookingReceiptSchema>;

export const bookingStatusUpdateSchema = z.object({
  status: bookingStatusSchema,
});
export type BookingStatusUpdateInput = z.infer<typeof bookingStatusUpdateSchema>;

export const bookingStatsSchema = z.object({
  newRequests: z.number().int(),
  confirmedThisMonth: z.number().int(),
  medianReplyHours: z.number().nullable(),
});
export type BookingStats = z.infer<typeof bookingStatsSchema>;

export const availabilityRangeSchema = z.object({
  checkIn: z.string(),
  checkOut: z.string(),
});
export type AvailabilityRange = z.infer<typeof availabilityRangeSchema>;

/** Nights between two `YYYY-MM-DD` dates. UTC maths keeps DST out of it. */
export function nightsBetween(checkIn: string, checkOut: string): number {
  const start = Date.parse(`${checkIn}T00:00:00Z`);
  const end = Date.parse(`${checkOut}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end)) return 0;
  return Math.round((end - start) / 86_400_000);
}
